import { useState, useRef } from "react";
import { ConnectorPosition } from "../types";
import { getAllDescendantUsernames } from "../utils";

export const useAvatarTree = (
  username: string,
  connectors: ConnectorPosition[]
) => {
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    {
      [username]: false,
    }
  );

  const [hoveredStates, setHoveredStates] = useState<Record<string, boolean>>(
    {}
  );
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleExpandClick = (targetUsername: string) => {
    setExpandedStates((prev) => {
      const isCurrentlyExpanded = prev[targetUsername];
      const newStates = { ...prev };

      if (isCurrentlyExpanded) {
        // Collapse and cascade to all descendants
        newStates[targetUsername] = false;

        const findAndCollapseDescendants = (nodes: ConnectorPosition[]) => {
          for (const node of nodes) {
            if (node.username === targetUsername) {
              if (node.children) {
                const descendants = getAllDescendantUsernames(node.children);
                descendants.forEach((descendant) => {
                  newStates[descendant] = false;
                });
              }
              return true;
            }
            if (node.children && findAndCollapseDescendants(node.children)) {
              return true;
            }
          }
          return false;
        };

        if (targetUsername === username) {
          // Main parent collapse
          const allDescendants = getAllDescendantUsernames(connectors);
          allDescendants.forEach((descendant) => {
            newStates[descendant] = false;
          });
        } else {
          findAndCollapseDescendants(connectors);
        }
      } else {
        // Just expand this node
        newStates[targetUsername] = true;
      }

      return newStates;
    });
  };

  const handleMouseEnter = (username: string, isParent: boolean = false) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (isParent) {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredStates({ [username]: true });
      }, 100);
    } else {
      setHoveredStates({ [username]: true });
    }
  };

  const handleMouseLeave = (username: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    setHoveredStates((prev) => ({
      ...prev,
      [username]: false,
    }));
  };

  return {
    expandedStates,
    hoveredStates,
    handleExpandClick,
    handleMouseEnter,
    handleMouseLeave,
  };
};
